import { ApplicationState } from '@/store';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { FilterKeyValues, TagType } from 'api-paths-cas';
import { TableCellProps } from '../types';
import { requestDashboardRows, requestTotals } from '@/store/offers-dashboard/actions';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { QueryKeys } from '@/types';

/**
 * Custom hook that loads the rows (leads or offers) and totals based on the
 * active tab, and returns the value.
 *
 * This hook listens to changes in relevant URL query parameters and the local storage uni and intake ID
 * to trigger a refresh of the rows.
 *
 */
export const useRows = (
  onTagDelete: (tagId: string, tagType: TagType | undefined, row: TableCellProps['row']) => void,
) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const relevantQueryKeys = [
    ...FilterKeyValues,
    QueryKeys.UNI,
    QueryKeys.INTAKE_ID,
    QueryKeys.SORT_COL,
    QueryKeys.SORT_DIR,
    QueryKeys.PAGE,
    QueryKeys.CURRENT_TAB,
    QueryKeys.SEARCH,
  ];

  const searchParams = new URLSearchParams(location.search);

  const keyValues = relevantQueryKeys.map(key => JSON.stringify(searchParams.getAll(key)));

  const { authIntake, authUni, currentUser } = useSelector(({ auth }: ApplicationState) => ({
    authIntake: auth.intake,
    authUni: auth.uni,
    currentUser: auth.currentUser,
  }));

  /**
   * Refresh rows when intake or uni changes, or when authIntake/authUni changes.
   */
  useEffect(() => {
    dispatch(requestDashboardRows());
    dispatch(requestTotals());
  }, [dispatch, authIntake, authUni, currentUser, ...keyValues]);

  const { rows } = useSelector(
    ({ offersDashboard }: ApplicationState) => ({
      rows: offersDashboard.dashboardRows?.map(row => ({ ...row, onTagDelete })) ?? [],
    }),
    shallowEqual,
  );

  return rows;
};
